// ─── Install prompt ────────────────────────────────────────────────────────
// SolFri is a fully installable PWA, but almost nobody discovers "Add to Home
// Screen" unprompted — installability that isn't surfaced may as well not
// exist. This offers it once, quietly, and remembers a decline.
//
// Browsers differ fundamentally in what they allow here, so this branches on
// capability rather than on brand:
//
//   Chrome / Edge / Samsung Internet / most Android browsers
//     Fire `beforeinstallprompt`. That event can be captured and replayed
//     later against a real button press, giving a genuine one-tap install.
//
//   iOS Safari
//     No install event and no programmatic install at all — Apple only permits
//     the manual Share → Add to Home Screen route. So it needs instructions,
//     and they have to be Safari-specific because the Share button lives in a
//     different place depending on iOS version.
//
//   iOS Chrome / Firefox / Edge
//     Cannot install PWAs at all on iOS: every iOS browser is required to use
//     WebKit, but only Safari exposes Add to Home Screen. Telling someone to
//     tap a button that isn't there is worse than saying nothing, so these get
//     an honest "open this in Safari" instead.
//
//   Desktop Chrome / Edge
//     Installable via the address-bar icon; the same captured event works.
//
//   Desktop Firefox / Safari
//     No PWA install support. Shown nothing rather than false instructions.

import React, { useState, useEffect } from "react";

// "Not now" snoozes rather than silences: someone who wasn't ready on a first
// visit shouldn't be permanently cut off from installing. Backs off each time
// so it never becomes nagging — and stops for good once actually installed.
const SNOOZE_UNTIL = "solfri_install_snooze_until";
const SNOOZE_COUNT = "solfri_install_snooze_count";
const INSTALLED_KEY = "solfri_install_done";
const DAY = 24 * 60 * 60 * 1000;
const BACKOFF_DAYS = [3, 7, 21, 60];   // then stops asking

function snoozed() {
  try {
    if (localStorage.getItem(INSTALLED_KEY)) return true;
    const until = parseInt(localStorage.getItem(SNOOZE_UNTIL) || "0", 10);
    return Date.now() < until;
  } catch { return false; }
}

function snooze() {
  try {
    const n = parseInt(localStorage.getItem(SNOOZE_COUNT) || "0", 10);
    if (n >= BACKOFF_DAYS.length) {
      // Asked enough times — stop for good rather than keep pestering.
      localStorage.setItem(INSTALLED_KEY, "asked-enough");
      return;
    }
    localStorage.setItem(SNOOZE_UNTIL, String(Date.now() + BACKOFF_DAYS[n] * DAY));
    localStorage.setItem(SNOOZE_COUNT, String(n + 1));
  } catch {}
}

function markInstalled() {
  try { localStorage.setItem(INSTALLED_KEY, "1"); } catch {}
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.matchMedia?.("(display-mode: fullscreen)")?.matches
    || window.navigator.standalone === true;          // iOS
}

// iPadOS 13+ reports itself as a Mac, so touch support is the reliable tell.
function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iphone|ipad|ipod/i.test(ua)
    || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
}

// On iOS, only genuine Safari can install. CriOS/FxiOS/EdgiOS are the other
// browsers' iOS identifiers — all WebKit underneath, none able to install.
function isIOSSafari() {
  if (!isIOSDevice()) return false;
  const ua = navigator.userAgent;
  return /safari/i.test(ua) && !/crios|fxios|edgios|opios|brave/i.test(ua);
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mode, setMode] = useState(null);      // 'button' | 'ios-safari' | 'ios-other'
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;                 // already installed
    if (snoozed()) return;

    let timer = null;

    // Capture the install event wherever it's offered. Registered immediately,
    // because the browser may fire it before any delay would elapse.
    const onPrompt = (e) => {
      e.preventDefault();                       // suppress the browser's own mini-infobar
      setDeferredPrompt(e);                     // capture immediately or it's lost
      // ...but don't ask straight away. Chrome can fire this the moment the
      // page loads, and asking someone to install something they haven't
      // looked at yet is the fastest way to get a reflexive "no".
      timer = setTimeout(() => setMode("button"), 25000);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // Once installed, get out of the way immediately.
    const onInstalled = () => { setMode(null); markInstalled(); };
    window.addEventListener("appinstalled", onInstalled);

    // iOS never fires the event, so fall back to instructions — but only after
    // someone has had a chance to actually use the app first.
    if (isIOSDevice()) {
      timer = setTimeout(() => {
        setMode(isIOSSafari() ? "ios-safari" : "ios-other");
      }, 40000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    setMode(null);
    setShowHelp(false);
    snooze();
  };

  const install = async () => {
    if (mode !== "button" || !deferredPrompt) { setShowHelp(true); return; }
    deferredPrompt.prompt();
    try {
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") { markInstalled(); setMode(null); }
      else setMode(null);   // cancelled the browser dialog — don't nag, but don't
                            // permanently record it either; they may want it later
    } catch { setMode(null); }
    setDeferredPrompt(null);
  };

  if (!mode) return null;

  const card = {
    pointerEvents:"auto", maxWidth:420, width:"100%",
    background:"linear-gradient(170deg,#FFFDFB 0%,#FFF6EE 100%)", borderRadius:18, padding:"15px 17px",
    boxShadow:"0 2px 6px rgba(92,61,46,0.10), 0 10px 28px rgba(92,61,46,0.15), 0 24px 56px rgba(92,61,46,0.12)",
  };
  const title = { fontFamily:"'Zen Old Mincho',Georgia,serif", fontSize:14.5, color:"#5C3D2E", margin:"0 0 5px", fontWeight:500 };
  const body  = { fontSize:12.5, color:"#8A7060", lineHeight:1.7, margin:"0 0 12px" };
  const primary = { flex:1, background:"#A0673A", color:"#fff", border:"none", borderRadius:10, padding:"10px 0",
    fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"inherit",
    boxShadow:"0 1px 2px rgba(92,61,46,0.12), 0 4px 12px rgba(160,103,58,0.26)" };
  const ghost = { background:"none", border:"none", color:"#A08070", fontSize:12.5, cursor:"pointer", fontFamily:"inherit", padding:"0 10px" };

  return (
    <div style={{position:"fixed",bottom:16,left:16,right:16,zIndex:200,display:"flex",justifyContent:"center",pointerEvents:"none"}}>
      <div style={card}>
        {/* iOS Safari — the manual route, described precisely */}
        {(mode === "ios-safari" || showHelp) && mode !== "ios-other" && (
          <>
            <p style={title}>Keep SolFri on your home screen</p>
            <p style={body}>
              Tap the <strong>Share</strong> icon — the square with an arrow, at the bottom of Safari
              (or top-right on iPad) — then scroll and choose <strong>Add to Home Screen</strong>.
            </p>
            <button onClick={dismiss} style={{...primary, background:"none", border:"1.5px solid #E8D5C4", color:"#8A7060", boxShadow:"none", width:"100%"}}>Got it</button>
          </>
        )}

        {/* Any iOS browser that isn't Safari — honest about the limitation */}
        {mode === "ios-other" && (
          <>
            <p style={title}>Want SolFri on your home screen?</p>
            <p style={body}>
              On iPhone and iPad, only Safari can add apps to the home screen. Open <strong>solfri.app</strong> in
              Safari and you'll find <strong>Add to Home Screen</strong> in the Share menu.
            </p>
            <button onClick={dismiss} style={{...primary, background:"none", border:"1.5px solid #E8D5C4", color:"#8A7060", boxShadow:"none", width:"100%"}}>Got it</button>
          </>
        )}

        {/* Real one-tap install */}
        {mode === "button" && !showHelp && (
          <>
            <p style={title}>Keep SolFri on your home screen</p>
            <p style={body}>Opens like any other app — its own icon, full screen, one tap away.</p>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <button onClick={install} style={primary}>Add to home screen</button>
              <button onClick={dismiss} style={ghost}>Not now</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
